/**
 * Inicis Billing Repository Implementation
 * KG이니시스 빌링(정기과금) 레포지토리 구현 (Data Layer)
 */

import { createOrder } from '../../domains/orders/services/orderServices';
import { createSubscription } from '../../domains/payments/services/subscriptionService';
import baseUrl from '../../utils/baseUrl';
import type { BillingRepository } from '../../domain/repositories/BillingRepository';
import type { BillingKeyIssueRequest } from '../../domain/models/payments/Billing';

const BILLING_URL = 'https://inilitepay.inicis.com/pay/card/billing';
const INILITE_KEY = 'UDdlS21qMkZLcU82YVFyY0J6cUVIZz09';
const MID = 'pocapanbil';

export class InicisBillingRepositoryImpl implements BillingRepository {
  async issueBillingKey(request: BillingKeyIssueRequest): Promise<void> {
    // 기본 검증(필수 필드)
    this.validate(request);
    // 해시 계산
    const hashdata = await this.computeHashData(
      request.price,
      MID,
      request.orderId,
      request.timestamp
    );

    const form = document.createElement('form');
    form.method = 'POST';
    form.action = BILLING_URL;
    form.acceptCharset = 'UTF-8';

    const appendField = (name: string, value: string) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = value;
      form.appendChild(input);
    };

    appendField('mid', MID);
    appendField('buyerTel', request.buyerTel);
    appendField('buyerEmail', request.buyerEmail);
    appendField('buyerName', request.buyerName);
    appendField('goodName', request.goodName);
    appendField('orderId', request.orderId);
    appendField('price', request.price);
    appendField('timestamp', request.timestamp);
    appendField('returnUrl', `${baseUrl()}/webhooks/v1/inicis/billkey_callback`);
    appendField('hashdata', hashdata);
    appendField('customData', request.customData ?? '');
    appendField('merchantRedirectData', request.merchantRedirectData ?? '');

    document.body.appendChild(form);
    form.submit();
    document.body.removeChild(form);
  }

  validate(r: BillingKeyIssueRequest): void {
    if (!r.productId) throw new Error('productId가 비어있습니다.');
    if (!r.price) throw new Error('금액이 비어있습니다.');
    if (!r.buyerName) throw new Error('구매자명이 비어있습니다.');
    if (!r.buyerEmail) throw new Error('구매자 이메일이 비어있습니다.');
    if (!r.buyerTel) throw new Error('구매자 전화번호가 비어있습니다.');
    if (!r.goodName) throw new Error('상품명이 비어있습니다.');
    if (!/^\d+$/.test(r.price)) throw new Error('금액은 숫자만 가능합니다.');
    if (!r.orderId) throw new Error('주문번호가 비어있습니다.');
    if (r.timestamp && !/^\d{14}$/.test(r.timestamp))
      throw new Error('timestamp 형식(YYYYMMDDhhmmss)이 잘못되었습니다.');
  }

  getCurrentTimestampYYYYMMDDhhmmss(): string {
    const pad = (n: number, w = 2) => n.toString().padStart(w, '0');
    const now = new Date();
    const yyyy = now.getFullYear();
    const MM = pad(now.getMonth() + 1);
    const dd = pad(now.getDate());
    const hh = pad(now.getHours());
    const mm = pad(now.getMinutes());
    const ss = pad(now.getSeconds());
    return `${yyyy}${MM}${dd}${hh}${mm}${ss}`;
  }

  async generateOrderId(productId: string): Promise<string> {
    const response = await createOrder({
      order: {
        items: [
          {
            product_id: productId,
            product_type: 'bettie_product' as const,
            quantity: 1
          }
        ]
      }
    });
    if (!response.orderId) {
      throw new Error('주문번호 생성에 실패했습니다');
    }
    return response.orderId;
  }

  async computeHashData(
    price: string,
    mid: string,
    orderId: string,
    timestamp: string
  ): Promise<string> {
    return this.generateHashData(price, mid, orderId, timestamp, INILITE_KEY);
  }

  /**
   * 구독 생성 수행
   * - URL 파라미터에서 billKey, orderId, status를 파싱
   * - 성공 여부를 boolean으로 반환
   */
  async createSubscriptionFromSearchParams(searchParams: URLSearchParams): Promise<boolean> {
    const billKey = searchParams.get('billKey');
    const orderId = searchParams.get('orderId');
    const status = searchParams.get('status');

    if (!billKey || !orderId || !status) {
      return false;
    }

    if (status !== '00') {
      return false;
    }
    // TODO: API 스키마에 맞춰서 수정 필요
    const body = {
      subscription: {
        auth_key: billKey,
        order_id: orderId,
        provider: 'inicis' as const,
        action: 'start' as const
      }
    };

    const response = await createSubscription(body);
    if (response?.subscriptionId) {
      return true;
    }
    return false;
  }

  private async generateHashData(
    price: string,
    mid: string,
    orderId: string,
    timestamp: string,
    iniLiteKey: string
  ): Promise<string> {
    const message = price + mid + orderId + timestamp + iniLiteKey;
    const encoder = new TextEncoder();
    const data = encoder.encode(message);
    const digest = await crypto.subtle.digest('SHA-512', data);
    const bytes = Array.from(new Uint8Array(digest));
    return bytes.map((b) => b.toString(16).padStart(2, '0')).join('');
  }
}

export const inicisBillingRepository = new InicisBillingRepositoryImpl();
